import React, { useEffect, useState, useContext } from 'react';
import { LanguageContext } from 'contexts/LanguageContext';
import {
	EditFilled,
	UserAddOutlined,
	WhatsAppOutlined,
	HighlightOutlined,
	LeftCircleOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { api } from 'services/api';
import { NumberConsumer } from 'contexts/legacy/NumberContext';
import { Card, Select, Input, Tooltip } from 'antd';
import message from '../../utils/messageWithScroll';
import PermissionIndex from './Permissions';
import '../../styles/createGroupIndex.css';
import { FormattedMessage } from 'react-intl';

function CreateGroupIndex({ number, setData }) {
	const { dictionary } = useContext(LanguageContext);
	const [users, setUsers] = useState([]);
	const [listOfNumbers, setListOfNumbers] = useState([]);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [selectedNumbers, setSelectedNumbers] = useState([]);
	const [groupName, setGroupName] = useState('');
	const [domain, setDomain] = useState('');
	const [isPermissions, setIsPermissions] = useState(false);
	const [isUsersSelected, setIsUsersSelected] = useState(false);
	const [isNumbersSelected, setIsNumbersSelected] = useState(false);
	const [isGroupNameFilled, setIsGroupNameFilled] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const { Option } = Select;

	useEffect(() => {
		setIsNumbersSelected(selectedNumbers.length > 0);
		setIsUsersSelected(selectedUsers.length > 0);
		setIsGroupNameFilled(groupName.trim() !== ''); // Verifica se o nome do grupo não está vazio
	}, [selectedNumbers, selectedUsers, groupName]);

	useEffect(() => {
		window.BX24.init(async () => {
			const auth = window.BX24 ? window.BX24.getAuth() : { domain: 'solinka.bitrix24.com' };
			const domain = auth.domain;
			setDomain(domain);

			await getUsersName(domain);
		});

		const getUsersName = async (domain) => {
			const request = {
				domain,
			};
			const users = await api.post('getAllUsers', request);
			setUsers(users.data);
		};

		setListOfNumbers([...number.numbers.actives, ...number.numbers.inactives]);
	}, [number.numbers.actives, number.numbers.inactives]);

	const handleSubmitPermission = async () => {
		if (isSaving) {
			return;
		}

		setIsSaving(true);

		await api
			.post('addPermissionGroup', {
				groupName,
				selectedNumbers,
				selectedUsers,
				domain,
			})
			.then((data) => {
				message.success(dictionary.create_group_success);

				setData((prevState) => {
					return [...prevState, data];
				});
			})
			.catch((err) => {
				if (err.message) {
					message.error(err.message);
				} else {
					message.error(dictionary.create_group_error);
				}
			});
		goToPermissions();
	};

	const goToPermissions = () => {
		setIsPermissions(true);
	};

	if (isPermissions) {
		return <PermissionIndex />;
	}

	return (
		<div id="create-group-content">
			<button
				role="button"
				className="text-bold btn-create-group btn-return"
				onClick={() => goToPermissions()}
			>
				<LeftCircleOutlined /> <FormattedMessage id="back" />{' '}
			</button>
			<div id="form-create-group">
				<Card
					title={
						<h3 className="text-bold title-create-group">
							<FormattedMessage id="create_new_permission_group" />{' '}
						</h3>
					}
					id="form-create-group"
				>
					<div>
						<label htmlFor="groupName" onClick={EditFilled}>
							<h3>
								<FormattedMessage id="group_name" />{' '}
								<Tooltip title={<FormattedMessage id="i_permission_group_name" />}>
									<InfoCircleOutlined
										style={{
											color: '#08AAF2',
										}}
									/>
								</Tooltip>
							</h3>
						</label>
						<HighlightOutlined className="icon-forms" />
						<Input
							id="groupName"
							type="text"
							name="groupName"
							className="input-forms"
							placeholder={dictionary.placeholder_group_name}
							defaultValue={groupName}
							onChange={(e) => {
								setGroupName(e.target.value);
							}}
						/>

						<label htmlFor="number">
							<h3>
								<FormattedMessage id="whatsapp_numbers" />{' '}
								<Tooltip title={<FormattedMessage id="i_whatsapp" />}>
									<InfoCircleOutlined
										style={{
											color: '#08AAF2',
										}}
									/>
								</Tooltip>
							</h3>
						</label>
						<WhatsAppOutlined className="icon-forms" />
						<Select
							id="number"
							name="number"
							size="large"
							className="input-forms"
							placeholder={<FormattedMessage id="placeholder_whats_app" />}
							mode="multiple"
							maxTagCount={2}
							maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length}`}
							defaultValue={selectedNumbers}
							onChange={(values) => setSelectedNumbers(values)}
						>
							{listOfNumbers.length &&
								listOfNumbers.map((number) => (
									<Option key={number.id} value={number.id}>
										{`${number.name} - ${number.number}`}
									</Option>
								))}
						</Select>

						<label htmlFor="users">
							<h3>
								<FormattedMessage id="users" />{' '}
								<Tooltip title={<FormattedMessage id="i_users" />}>
									<InfoCircleOutlined
										style={{
											color: '#08AAF2',
										}}
									/>
								</Tooltip>
							</h3>
						</label>
						<UserAddOutlined className="icon-forms" />
						<Select
							name="users"
							size="large"
							className="input-forms"
							placeholder={<FormattedMessage id="placeholder_users" />}
							mode="multiple"
							maxTagCount={2}
							maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length}`}
							value={selectedUsers.map((user) => user.id)}
							onChange={(values) => {
								if (values.includes(0)) {
									setSelectedUsers([{ id: 0, fullName: 'User with ID 0' }]);
								} else {
									const selectedUsersWithFullName = values?.map((userId) => {
										const user = users.find((u) => u.id === userId);
										return { id: userId, fullName: user?.fullName || '' };
									});
									setSelectedUsers(selectedUsersWithFullName);
								}
							}}
							required
							showSearch
							optionFilterProp="children"
							filterOption={(input, option) =>
								option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							notFoundContent={dictionary.user_not_found}
						>
							{users.map((user) => (
								<Option key={user.id} value={user.id}>
									{user?.fullName || ''}
								</Option>
							))}
						</Select>
					</div>
					<button
						onClick={handleSubmitPermission}
						className="text-bold btn-create-group btn-save"
						disabled={!isUsersSelected || !isNumbersSelected || !isGroupNameFilled || isSaving}
						style={{ cursor: 'pointer' }}
					>
						<FormattedMessage id="save" />
					</button>
				</Card>
			</div>
		</div>
	);
}

export default NumberConsumer(CreateGroupIndex);
